// frontend/src/lib/remediationPlaybooks.js

function safeArray(x) {
    return Array.isArray(x) ? x : [];
  }
  
  function safeObj(x) {
    return x && typeof x === "object" && !Array.isArray(x) ? x : {};
  }
  
  function str(x) {
    return String(x || "").trim();
  }
  
  function upper(x) {
    return str(x).toUpperCase();
  }
  
  export const GRAVITY_BANDS = {
    LOW: "LOW",
    GUARDED: "GUARDED",
    SIGNIFICANT: "SIGNIFICANT",
    HIGH: "HIGH",
    CRITICAL: "CRITICAL",
  };
  
  export function normalizeGravityValue(input) {
    const n = Number(input || 0);
    if (n >= 9) return 10;
    if (n >= 7) return 8;
    if (n >= 5) return 6;
    if (n >= 3) return 4;
    return 2;
  }
  
  export function getGravityBand(input) {
    const g = normalizeGravityValue(input);
    if (g >= 10) return GRAVITY_BANDS.CRITICAL;
    if (g >= 8) return GRAVITY_BANDS.HIGH;
    if (g >= 6) return GRAVITY_BANDS.SIGNIFICANT;
    if (g >= 4) return GRAVITY_BANDS.GUARDED;
    return GRAVITY_BANDS.LOW;
  }
  
  export function getGravityBandLabel(input) {
    const band = getGravityBand(input);
    if (band === GRAVITY_BANDS.CRITICAL) return "Critical";
    if (band === GRAVITY_BANDS.HIGH) return "High";
    if (band === GRAVITY_BANDS.SIGNIFICANT) return "Significant";
    if (band === GRAVITY_BANDS.GUARDED) return "Guarded";
    return "Low";
  }
  
  export function formatRemediationLabel(remediationLike = {}, fallbackCategory = "") {
    const remediation = safeObj(remediationLike);
    const label = str(remediation.playbookLabel || fallbackCategory || "Recommended Remediation");
    const urgency = str(remediation.gravityBandLabel);
    return urgency ? `${label} (${urgency})` : label;
  }
  
  export function getPrioritySortWeight(rowLike = {}) {
    const row = safeObj(rowLike);
    const status = str(row.status).toLowerCase();
    const statusWeight = status === "open" ? 40 : status === "in_progress" ? 30 : status === "mitigated" ? 20 : status === "closed" ? 10 : 0;
    const gravityWeight = normalizeGravityValue(row.gravity) * 1000000;
    const exposureWeight = Math.round(Number(row.exposure_high || 0));
    return gravityWeight + exposureWeight + statusWeight;
  }
  
  function uniqStrings(list) {
    return Array.from(new Set(safeArray(list).map((x) => str(x)).filter(Boolean)));
  }
  
  function sentenceList(items = []) {
    return uniqStrings(items).map((item) => `• ${item}`).join("\n");
  }
  
  const PLAYBOOKS = {
    AI_RISK_MANAGEMENT: {
      id: "AI_RISK_MANAGEMENT",
      label: "AI Risk Management Framework",
      summary:
        "Formalize the operating framework for how AI risks are identified, approved, reviewed, and escalated across the business.",
      priorityActions: {
        LOW: [
          "Confirm the business has a current inventory of active AI use cases and decision owners.",
          "Set a simple review cadence so leadership can revisit AI risks at least quarterly.",
          "Document the current control environment and note the largest remaining gaps.",
        ],
        MEDIUM: [
          "Stand up a named AI risk owner and define the escalation path for material issues.",
          "Adopt a repeatable intake and review workflow before new AI use cases go live.",
          "Create a remediation tracker with dates, owners, and verification criteria.",
        ],
        HIGH: [
          "Pause expansion of affected AI use cases until governance approval is documented.",
          "Run an urgent gap assessment covering policy, testing, monitoring, and incident response.",
          "Issue an executive action plan with accountable owners and near-term deadlines.",
        ],
      },
      fullActions: [
        "Assign executive accountability for the AI control environment and define delegated owners.",
        "Maintain an AI system inventory covering purpose, data sources, outputs, and impacted users.",
        "Require documented risk review before launch, major change, or new deployment context.",
        "Define approval thresholds for higher-risk use cases and create an escalation path for exceptions.",
        "Track control gaps, remediation dates, and evidence of completion in one place.",
        "Run a periodic governance review to reassess risk, business value, and residual exposure.",
        "Train relevant teams on the approval workflow, prohibited practices, and evidence expectations.",
        "Prepare a concise board-ready summary of material AI risks and remediation status.",
      ],
      legalChecklist: [
        "AI governance policy or control framework.",
        "Named owners and approval matrix.",
        "Risk review records for active AI systems.",
        "Remediation tracker with dates and closure evidence.",
        "Management reporting pack or governance minutes.",
      ],
      evidenceFocus: [
        "Policy versions and approval history.",
        "Committee or management meeting minutes.",
        "System inventory extracts.",
        "Remediation tickets and sign-off records.",
        "Training completion evidence.",
      ],
    },
    HUMAN_OVERSIGHT: {
      id: "HUMAN_OVERSIGHT",
      label: "Human Oversight",
      summary:
        "Ensure meaningful human review exists where AI outputs could materially affect people, rights, or important business decisions.",
      priorityActions: {
        LOW: [
          "Confirm where human review already exists and document decision checkpoints.",
          "Clarify when staff may override or reject AI outputs.",
          "Provide short guidance to reviewers on what to look for.",
        ],
        MEDIUM: [
          "Require human sign-off for higher-impact outputs before action is taken.",
          "Define escalation triggers for unusual, harmful, or low-confidence results.",
          "Record overrides and manual interventions so patterns can be reviewed.",
        ],
        HIGH: [
          "Immediately stop fully automated decisions in the affected workflow until safeguards are restored.",
          "Implement mandatory second-line review for sensitive or irreversible outcomes.",
          "Retrospectively review recent decisions for potential harm or bias.",
        ],
      },
      fullActions: [
        "Map where AI outputs influence decisions, actions, or customer interactions.",
        "Define which decisions require human approval, challenge, or sampling review.",
        "Train reviewers to recognize hallucinations, bias indicators, and unsafe edge cases.",
        "Provide reviewers with the context needed to understand input limits and output confidence.",
        "Create clear override, escalation, and incident referral procedures.",
        "Keep records of manual interventions and review them for recurring patterns.",
        "Test whether oversight is substantive in practice rather than only nominal on paper.",
        "Update SOPs so human oversight remains in place after product or workflow changes.",
      ],
      legalChecklist: [
        "Documented human review checkpoints.",
        "Reviewer SOPs and training materials.",
        "Override and escalation logs.",
        "Sampling or QA review records.",
        "Retrospective review notes for affected cases.",
      ],
      evidenceFocus: [
        "Workflow diagrams showing decision gates.",
        "Reviewer sign-off records.",
        "Exception or override logs.",
        "QA reports.",
        "Escalation tickets.",
      ],
    },
    ACCOUNTABILITY_GOVERNANCE: {
      id: "ACCOUNTABILITY_GOVERNANCE",
      label: "Accountability and Governance",
      summary:
        "Clarify ownership, decision rights, and control accountability so AI risks are managed consistently rather than informally.",
      priorityActions: {
        LOW: [
          "Confirm accountable owners for the affected process and related controls.",
          "Document the current approval path for new or changed AI use cases.",
          "Close obvious ownership gaps that are preventing timely remediation.",
        ],
        MEDIUM: [
          "Define first-line and second-line responsibilities for the affected use case.",
          "Require formal sign-off before material AI changes are promoted.",
          "Add governance reporting for unresolved high-priority findings.",
        ],
        HIGH: [
          "Escalate the issue to senior leadership and assign direct executive accountability.",
          "Freeze non-essential changes until ownership and approval defects are corrected.",
          "Launch a time-bound governance remediation plan with weekly oversight.",
        ],
      },
      fullActions: [
        "Assign clear business, technical, legal, and compliance owners.",
        "Define approval authority for launches, exceptions, and material model changes.",
        "Implement a governance forum or decision mechanism for higher-risk AI systems.",
        "Record who approved what, when, and on what basis.",
        "Require periodic certification that key controls remain operational.",
        "Report unresolved control failures to leadership with deadlines and owners.",
        "Link remediation closure to evidence rather than verbal confirmation.",
        "Review the governance structure after major incidents or product changes.",
      ],
      legalChecklist: [
        "RACI or ownership matrix.",
        "Approval records.",
        "Governance meeting outputs.",
        "Control attestations.",
        "Leadership escalation records.",
      ],
      evidenceFocus: [
        "Committee charters.",
        "Approval templates.",
        "Decision logs.",
        "Attestation records.",
        "Leadership reports.",
      ],
    },
    INCIDENT_REPORTING: {
      id: "INCIDENT_REPORTING",
      label: "Incident Reporting",
      summary:
        "Ensure AI incidents, near misses, and material control failures are identified quickly, escalated properly, and documented defensibly.",
      priorityActions: {
        LOW: [
          "Confirm what the business currently treats as an AI incident or near miss.",
          "Define who should be notified when a material issue is detected.",
          "Capture recent events consistently so trends can be reviewed.",
        ],
        MEDIUM: [
          "Introduce a formal AI incident intake and severity triage process.",
          "Set required internal notification timelines and responsible roles.",
          "Link incident review to corrective action and control improvement.",
        ],
        HIGH: [
          "Escalate active incidents immediately and preserve logs, outputs, and decision records.",
          "Activate crisis or legal review if people, regulators, or critical services may be affected.",
          "Suspend the affected workflow until containment and root-cause review are underway.",
        ],
      },
      fullActions: [
        "Define reportable AI incidents, near misses, and material control failures.",
        "Create a standard intake template capturing facts, timing, impact, and actions taken.",
        "Assign incident owners and backup owners.",
        "Set escalation thresholds for legal, compliance, privacy, security, and leadership review.",
        "Preserve evidence needed for root-cause analysis and defensibility.",
        "Track corrective actions through to verified closure.",
        "Review incident trends to identify recurring weaknesses.",
        "Include AI incidents in management reporting and lessons-learned processes.",
      ],
      legalChecklist: [
        "Incident classification criteria.",
        "Incident logs and timelines.",
        "Preservation of evidence records.",
        "Corrective action tracker.",
        "Lessons-learned outputs.",
      ],
      evidenceFocus: [
        "Incident tickets.",
        "Escalation emails or approvals.",
        "System logs and screenshots.",
        "Root-cause analysis notes.",
        "Closure verification evidence.",
      ],
    },
    TRAINING_DATA_GOVERNANCE: {
      id: "TRAINING_DATA_GOVERNANCE",
      label: "Training Data Governance",
      summary:
        "Bring discipline to how training and fine-tuning data is sourced, approved, reviewed, and refreshed.",
      priorityActions: {
        LOW: [
          "List the main data sources used for training or fine-tuning and confirm who approved them.",
          "Document known limitations in provenance, quality, or permissions.",
          "Set a review date for older datasets.",
        ],
        MEDIUM: [
          "Introduce minimum approval checks for provenance, rights, quality, and relevance.",
          "Require versioning so training changes can be traced over time.",
          "Define refresh and retirement rules for stale or risky datasets.",
        ],
        HIGH: [
          "Stop use of unverified or high-risk data sources pending review.",
          "Run an urgent provenance and rights assessment on the affected datasets.",
          "Reassess recent model changes to determine whether retraining or rollback is required.",
        ],
      },
      fullActions: [
        "Maintain a register of datasets used for training, fine-tuning, and evaluation.",
        "Document source, provenance, permissions, and intended use for each dataset.",
        "Apply quality, relevance, and representativeness review before use.",
        "Version datasets and keep change records for additions or removals.",
        "Define retention and refresh periods for training inputs.",
        "Screen for sensitive, prohibited, or low-integrity content.",
        "Approve exceptions through a documented governance path.",
        "Retain evidence that dataset reviews actually occurred.",
      ],
      legalChecklist: [
        "Dataset inventory.",
        "Provenance and rights records.",
        "Quality review checklists.",
        "Version history.",
        "Exception approvals.",
      ],
      evidenceFocus: [
        "Dataset metadata exports.",
        "Source approvals.",
        "Review notes.",
        "Version diffs.",
        "Exception logs.",
      ],
    },
    DATASET_BIAS: {
      id: "DATASET_BIAS",
      label: "Dataset Bias and Representativeness",
      summary:
        "Assess whether data used to develop or operate the system creates unfair distortion, under-representation, or harmful performance gaps.",
      priorityActions: {
        LOW: [
          "Document where bias or representativeness concerns are currently known or suspected.",
          "Identify the populations most likely to be affected by performance disparities.",
          "Plan targeted testing rather than relying on general accuracy alone.",
        ],
        MEDIUM: [
          "Run segmented evaluation to detect material performance differences across relevant groups.",
          "Define acceptance thresholds and escalation triggers for disparity findings.",
          "Implement targeted data or control improvements where gaps are found.",
        ],
        HIGH: [
          "Escalate immediately if outputs may create discriminatory or systematically unfair outcomes.",
          "Pause sensitive use cases until disparity testing and mitigation are completed.",
          "Review recent affected decisions and prepare a corrective action record.",
        ],
      },
      fullActions: [
        "Define fairness and representativeness risks relevant to the use case.",
        "Evaluate dataset composition for under-coverage, skew, or outdated patterns.",
        "Run segmented model testing across relevant populations or contexts.",
        "Record performance gaps and explain mitigation decisions.",
        "Improve sampling, labeling, or controls where disparities are material.",
        "Review whether the use case remains appropriate if residual bias is high.",
        "Repeat testing after major model or data changes.",
        "Maintain evidence showing fairness review is ongoing, not one-off.",
      ],
      legalChecklist: [
        "Fairness assessment criteria.",
        "Segmented testing outputs.",
        "Mitigation decision records.",
        "Residual risk sign-off.",
        "Post-change retesting records.",
      ],
      evidenceFocus: [
        "Test reports.",
        "Sampling analysis.",
        "Label review notes.",
        "Mitigation tickets.",
        "Approval records.",
      ],
    },
    DATA_PROTECTION_PRIVACY: {
      id: "DATA_PROTECTION_PRIVACY",
      label: "Data Protection and Privacy",
      summary:
        "Make sure personal data used by or exposed through AI is handled lawfully, proportionately, and with defensible controls.",
      priorityActions: {
        LOW: [
          "Confirm what personal data the AI workflow touches and why it is needed.",
          "Check whether current notices and internal records reflect actual data use.",
          "Close obvious retention or access-control gaps.",
        ],
        MEDIUM: [
          "Run or refresh the applicable privacy impact assessment for the use case.",
          "Tighten data minimization, access controls, and retention settings.",
          "Confirm vendor and processor terms cover the live data flows.",
        ],
        HIGH: [
          "Contain high-risk processing immediately if personal or sensitive data is being mishandled.",
          "Escalate to privacy and legal review with preserved evidence of the data flows involved.",
          "Suspend non-essential processing until legal basis, notices, and controls are verified.",
        ],
      },
      fullActions: [
        "Map personal data inputs, outputs, and downstream sharing points.",
        "Verify legal basis, notices, and internal records of processing are aligned to the actual use case.",
        "Apply data minimization and role-based access controls.",
        "Set retention and deletion rules for prompts, outputs, logs, and training artifacts.",
        "Review cross-border transfer and vendor risk implications where relevant.",
        "Refresh privacy impact assessment documentation for material changes.",
        "Test whether the system can support data subject rights and complaint handling.",
        "Retain evidence of approvals, notices, and control operation.",
      ],
      legalChecklist: [
        "Data flow map.",
        "Privacy impact assessment or equivalent.",
        "Notice language and records of processing.",
        "Access and retention control records.",
        "Vendor/DPA documentation.",
      ],
      evidenceFocus: [
        "Privacy review approvals.",
        "Screenshots of notices.",
        "Access-control extracts.",
        "Retention settings.",
        "Vendor contract clauses.",
      ],
    },
    RECORDKEEPING_RETENTION: {
      id: "RECORDKEEPING_RETENTION",
      label: "Recordkeeping and Retention",
      summary:
        "Ensure the business keeps the right records long enough to show what happened, who approved it, and how issues were addressed.",
      priorityActions: {
        LOW: [
          "Confirm which records already exist and where they are stored.",
          "Remove obvious ambiguity about record owners and retention periods.",
          "Set minimum retention expectations for current remediation evidence.",
        ],
        MEDIUM: [
          "Implement a standard evidence pack for approvals, testing, monitoring, and remediation.",
          "Define retention periods by record type and operational need.",
          "Check that records can be retrieved quickly if challenged.",
        ],
        HIGH: [
          "Preserve records immediately for the affected workflow and suspend routine deletion if necessary.",
          "Escalate missing records as a defensibility issue requiring urgent closure.",
          "Run a remediation sprint to reconstruct critical missing documentation.",
        ],
      },
      fullActions: [
        "Define the minimum records required for each AI system and control domain.",
        "Assign record owners and storage locations.",
        "Create retention and deletion rules that align with legal and operational needs.",
        "Preserve approvals, testing, monitoring, incidents, and remediation evidence.",
        "Make records searchable and retrievable for audits or disputes.",
        "Periodically test retrieval so records are not only theoretically available.",
        "Close gaps where evidence is scattered across tools or inboxes.",
        "Document exceptions and temporary workarounds until the record system is stabilized.",
      ],
      legalChecklist: [
        "Record taxonomy.",
        "Retention schedule.",
        "Storage ownership matrix.",
        "Retrieval test results.",
        "Exception log.",
      ],
      evidenceFocus: [
        "Retention policy.",
        "Storage screenshots.",
        "Sample evidence packs.",
        "Retrieval test records.",
        "Preservation notices.",
      ],
    },
    MODEL_TESTING_VALIDATION: {
      id: "MODEL_TESTING_VALIDATION",
      label: "Model Testing and Validation",
      summary:
        "Demonstrate that the model has been tested appropriately for the way it is actually being used in production.",
      priorityActions: {
        LOW: [
          "Document the current validation approach and where coverage is thin.",
          "Confirm which test scenarios matter most for this use case.",
          "Set a date to refresh stale test evidence.",
        ],
        MEDIUM: [
          "Introduce scenario-based validation aligned to the real operating context.",
          "Define pass/fail thresholds and escalation rules for material gaps.",
          "Retest after material data, model, or workflow changes.",
        ],
        HIGH: [
          "Do not rely on the model for the affected workflow until high-risk validation gaps are closed.",
          "Run urgent targeted testing on known failure modes and edge cases.",
          "Escalate results to leadership if recent outputs may have caused harm or misstatements.",
        ],
      },
      fullActions: [
        "Define validation objectives that reflect the live use case, not only lab performance.",
        "Create test scenarios covering ordinary, edge, and failure conditions.",
        "Measure accuracy, consistency, and harmful error patterns.",
        "Record validation methods, assumptions, and known limitations.",
        "Set approval thresholds and review requirements for unresolved weaknesses.",
        "Retest after model, prompt, data, or policy changes.",
        "Store validation outputs so they can be reviewed later.",
        "Use validation findings to drive remediation priorities and go-live decisions.",
      ],
      legalChecklist: [
        "Validation plan.",
        "Scenario library.",
        "Test outputs.",
        "Approval thresholds.",
        "Retesting records.",
      ],
      evidenceFocus: [
        "Test reports.",
        "Scenario matrices.",
        "Approval sign-offs.",
        "Change-triggered retesting evidence.",
        "Issue logs.",
      ],
    },
    MONITORING_DRIFT: {
      id: "MONITORING_DRIFT",
      label: "Monitoring and Drift Detection",
      summary:
        "Establish live monitoring so deteriorating performance, unsafe behavior, and control failures are identified before they become larger exposure events.",
      priorityActions: {
        LOW: [
          "Confirm what is currently monitored and where blind spots remain.",
          "Define a minimum set of health indicators for the affected workflow.",
          "Make sure someone owns review of monitoring outputs.",
        ],
        MEDIUM: [
          "Add threshold-based alerts for performance, drift, abuse, and policy breaches.",
          "Set a formal review cadence for monitoring results and unresolved alerts.",
          "Feed monitoring issues into the remediation tracker with accountable owners.",
        ],
        HIGH: [
          "Implement immediate alerting and leadership escalation for critical signals.",
          "Increase manual review while the control gap remains open.",
          "Investigate whether recent outputs should be quarantined, corrected, or withdrawn.",
        ],
      },
      fullActions: [
        "Define live metrics that matter for the use case, including quality and harm indicators.",
        "Implement monitoring for drift, failure spikes, abuse, and policy violations.",
        "Set escalation thresholds and named responders.",
        "Review monitoring outputs on a documented cadence.",
        "Retain evidence of alert handling and follow-up actions.",
        "Test whether monitoring actually detects the intended failure modes.",
        "Integrate monitoring signals into governance reporting.",
        "Adjust controls and retrain where monitoring shows persistent degradation.",
      ],
      legalChecklist: [
        "Monitoring metric definitions.",
        "Alert thresholds.",
        "Responder matrix.",
        "Alert review records.",
        "Follow-up remediation evidence.",
      ],
      evidenceFocus: [
        "Dashboards or screenshots.",
        "Alert logs.",
        "Responder notes.",
        "Trend reviews.",
        "Corrective action tickets.",
      ],
    },
    SECURITY_ROBUSTNESS: {
      id: "SECURITY_ROBUSTNESS",
      label: "Security and Adversarial Robustness",
      summary:
        "Protect the AI workflow against misuse, data leakage, prompt attacks, unauthorized access, and operational compromise.",
      priorityActions: {
        LOW: [
          "Confirm the main security assumptions for the workflow and its dependencies.",
          "Close obvious access-control or configuration gaps.",
          "Document the highest-priority misuse scenarios.",
        ],
        MEDIUM: [
          "Introduce targeted testing for prompt injection, abuse, leakage, and unsafe tool actions.",
          "Tighten access controls, secrets handling, and environment separation.",
          "Add monitoring for anomalous use and abuse attempts.",
        ],
        HIGH: [
          "Contain the affected workflow immediately if exploitation or material leakage is plausible.",
          "Rotate exposed credentials or tokens and preserve forensic evidence.",
          "Escalate to security leadership and validate whether customer or regulator notification may be needed.",
        ],
      },
      fullActions: [
        "Run threat modeling specific to the AI workflow and its integrations.",
        "Test for prompt injection, abuse, data leakage, and unsafe action execution.",
        "Apply least-privilege access controls for data, prompts, logs, and tools.",
        "Protect secrets, API keys, and admin functions.",
        "Monitor for abuse, anomalous access, and unusual output patterns.",
        "Document incident containment and recovery steps.",
        "Retest after major changes or newly discovered threats.",
        "Coordinate AI-specific controls with the broader security program.",
      ],
      legalChecklist: [
        "Threat model.",
        "Security test results.",
        "Access-control evidence.",
        "Secrets management records.",
        "Incident response linkage.",
      ],
      evidenceFocus: [
        "Pen-test or security review outputs.",
        "Configuration screenshots.",
        "Access logs.",
        "Alert records.",
        "Containment tickets.",
      ],
    },
    MODEL_DOCUMENTATION: {
      id: "MODEL_DOCUMENTATION",
      label: "Model Documentation and Traceability",
      summary:
        "Document how the system works well enough that the business can explain, defend, review, and change it responsibly.",
      priorityActions: {
        LOW: [
          "Identify which core documents already exist and which are outdated or missing.",
          "Clarify the minimum documentation set for this use case.",
          "Assign owners for missing documents.",
        ],
        MEDIUM: [
          "Create or refresh core system documentation before the next major release.",
          "Link documentation to approvals, testing, and monitoring evidence.",
          "Version control material changes so the operating history is clear.",
        ],
        HIGH: [
          "Treat missing core documentation as a material governance gap requiring immediate closure.",
          "Hold release or expansion activity until critical traceability records exist.",
          "Reconstruct the recent change history for the affected system.",
        ],
      },
      fullActions: [
        "Maintain a concise description of the model, purpose, users, and deployment context.",
        "Document key assumptions, limitations, and known failure modes.",
        "Link system documentation to approvals, validation, monitoring, and incidents.",
        "Version documents when material changes occur.",
        "Make documentation accessible to responsible teams.",
        "Retain a change history for model, prompt, tool, and workflow updates.",
        "Use documentation as part of launch and change approval.",
        "Review documents periodically for accuracy.",
      ],
      legalChecklist: [
        "System description.",
        "Known limitations log.",
        "Version history.",
        "Approval linkage.",
        "Periodic review evidence.",
      ],
      evidenceFocus: [
        "Design docs.",
        "Release notes.",
        "Architecture diagrams.",
        "Model cards or equivalent.",
        "Review sign-offs.",
      ],
    },
    TRANSPARENCY_DISCLOSURE: {
      id: "TRANSPARENCY_DISCLOSURE",
      label: "Transparency and Disclosure",
      summary:
        "Make sure users and stakeholders are told enough about the AI system, its role, and its limits to avoid misleading use.",
      priorityActions: {
        LOW: [
          "Review what users are currently told about AI involvement.",
          "Close obvious clarity gaps in notices, disclaimers, or internal scripts.",
          "Check whether frontline teams understand the current messaging.",
        ],
        MEDIUM: [
          "Refresh customer-facing and internal disclosures so they match the live product behavior.",
          "Add clear explanation of when AI is used and what users should not rely on without review.",
          "Create an approval process for future disclosure changes.",
        ],
        HIGH: [
          "Correct misleading statements immediately if users may be relying on inaccurate representations.",
          "Escalate potential consumer or regulatory exposure to legal/compliance review.",
          "Add interim warnings or manual explanations until permanent fixes are live.",
        ],
      },
      fullActions: [
        "Identify where AI use is disclosed to users, customers, staff, or other stakeholders.",
        "Align disclosure language with the actual product workflow and limitations.",
        "Avoid statements that overclaim accuracy, autonomy, or safety.",
        "Explain important limits, human review points, and complaint pathways where relevant.",
        "Review disclosures after major product changes.",
        "Train customer-facing teams on approved representations.",
        "Retain approval evidence for material disclosure language.",
        "Test whether disclosures are understandable in practice.",
      ],
      legalChecklist: [
        "Disclosure inventory.",
        "Approved language versions.",
        "Review sign-offs.",
        "Training records.",
        "Post-change refresh evidence.",
      ],
      evidenceFocus: [
        "UI screenshots.",
        "Website/app copy approvals.",
        "Script or FAQ versions.",
        "Training notes.",
        "Customer communication samples.",
      ],
    },
    EXPLAINABILITY: {
      id: "EXPLAINABILITY",
      label: "Explainability",
      summary:
        "Ensure the organization can explain outputs and decisions at a level appropriate to the use case, the stakes, and the audience.",
      priorityActions: {
        LOW: [
          "Confirm what level of explanation is realistically needed for this workflow.",
          "Document the main drivers or inputs that influence outputs.",
          "Clarify when staff should give additional context to users.",
        ],
        MEDIUM: [
          "Create a standard explanation approach for reviewers, customers, or affected users.",
          "Test whether explanations are accurate, consistent, and understandable.",
          "Record where explanation limits remain.",
        ],
        HIGH: [
          "Stop using the output for sensitive decisions if it cannot be explained adequately.",
          "Escalate cases where unexplainable outputs may have driven adverse outcomes.",
          "Implement a manual review fallback until explanation quality is acceptable.",
        ],
      },
      fullActions: [
        "Define the explanation needs for internal reviewers and external users.",
        "Document the main factors, assumptions, and limits influencing outputs.",
        "Provide reviewers with guidance on how to explain or challenge outputs.",
        "Avoid using unexplainable outputs for decisions that require justification.",
        "Test explanation quality using real scenarios.",
        "Refresh explanation materials when the model or workflow changes.",
        "Track complaints or confusion linked to weak explanations.",
        "Retain evidence of explanation design and review.",
      ],
      legalChecklist: [
        "Explanation standard.",
        "Reviewer guidance.",
        "Explanation testing outputs.",
        "Fallback process records.",
        "Complaint trend review.",
      ],
      evidenceFocus: [
        "Reviewer guidance docs.",
        "Scenario test results.",
        "User support scripts.",
        "Complaint records.",
        "Approval history.",
      ],
    },
    USER_REDRESS: {
      id: "USER_REDRESS",
      label: "User Redress",
      summary:
        "Give affected users a practical path to question outcomes, escalate concerns, and obtain human review or correction where needed.",
      priorityActions: {
        LOW: [
          "Check whether users currently know how to raise concerns or request review.",
          "Confirm who handles complaints or corrections for this workflow.",
          "Close obvious routing gaps.",
        ],
        MEDIUM: [
          "Implement a documented redress and review pathway with response ownership.",
          "Train support teams on how to escalate AI-related complaints.",
          "Track outcomes so recurring issues can inform remediation.",
        ],
        HIGH: [
          "Prioritize human review for potentially harmful or high-impact complaints immediately.",
          "Escalate unresolved adverse outcomes to legal/compliance review.",
          "Consider proactive outreach or correction if recent users may have been affected.",
        ],
      },
      fullActions: [
        "Define how users can challenge AI-driven outcomes or raise concerns.",
        "Assign ownership for complaint intake, investigation, and response.",
        "Provide a human review path for material disputes.",
        "Set response targets and escalation rules.",
        "Record complaints, decisions, and corrective actions.",
        "Analyze complaint trends for control weaknesses.",
        "Link redress outcomes to product or policy improvements.",
        "Retain evidence showing the redress process is operational.",
      ],
      legalChecklist: [
        "Complaint process design.",
        "Ownership matrix.",
        "Complaint logs.",
        "Human review records.",
        "Trend review outputs.",
      ],
      evidenceFocus: [
        "Support workflow screenshots.",
        "Complaint samples.",
        "Escalation records.",
        "Corrective action tickets.",
        "Reporting packs.",
      ],
    },
    SOCIAL_SCORING: {
      id: "SOCIAL_SCORING",
      label: "Social Scoring",
      summary:
        "Prevent use cases that rate or rank people in ways that create disproportionate or inappropriate downstream effects.",
      priorityActions: {
        LOW: [
          "Confirm whether any ranking or profiling features could drift toward social scoring behavior.",
          "Document current safeguards and intended limits.",
          "Review the downstream actions attached to those scores or profiles.",
        ],
        MEDIUM: [
          "Restrict use of scoring outputs to clearly approved contexts with oversight.",
          "Remove downstream uses that exceed the stated purpose or fairness guardrails.",
          "Add governance review before new ranking dimensions are introduced.",
        ],
        HIGH: [
          "Stop the affected scoring practice immediately if it may cross into prohibited or highly sensitive territory.",
          "Escalate to legal/compliance review and preserve evidence of how scores were used.",
          "Review prior uses for inappropriate downstream consequences.",
        ],
      },
      fullActions: [
        "Map how people are scored, ranked, or profiled in the workflow.",
        "Confirm the legitimate purpose and approved limits of that scoring.",
        "Assess whether downstream uses are disproportionate or inappropriate.",
        "Restrict or remove sensitive uses that create unfair pressure or exclusion.",
        "Require governance approval for new profiling dimensions.",
        "Maintain a record of review decisions and residual risk.",
        "Monitor for scope creep over time.",
        "Retain evidence showing prohibited or overreaching uses were blocked.",
      ],
      legalChecklist: [
        "Scoring use-case map.",
        "Purpose limitation review.",
        "Downstream use approval records.",
        "Restriction controls.",
        "Periodic monitoring evidence.",
      ],
      evidenceFocus: [
        "Workflow diagrams.",
        "Approval notes.",
        "Feature flags or config evidence.",
        "Monitoring outputs.",
        "Issue logs.",
      ],
    },
    VULNERABLE_PERSONS: {
      id: "VULNERABLE_PERSONS",
      label: "Exploitation of Vulnerable Individuals",
      summary:
        "Prevent AI design or use patterns that take advantage of children, distressed users, or others in vulnerable positions.",
      priorityActions: {
        LOW: [
          "Identify whether vulnerable users may reasonably be reached or affected.",
          "Document current safeguards and messaging protections.",
          "Review content or decision pathways that could create pressure or manipulation.",
        ],
        MEDIUM: [
          "Add stronger safeguards for vulnerable audiences, including review and escalation triggers.",
          "Tighten content, targeting, and intervention rules.",
          "Train staff to recognize and escalate vulnerability-related risks.",
        ],
        HIGH: [
          "Pause the affected workflow immediately if vulnerable users may be exposed to harmful manipulation.",
          "Escalate to legal/compliance and product leadership for urgent containment.",
          "Review recent interactions for harm and corrective follow-up.",
        ],
      },
      fullActions: [
        "Assess whether children or vulnerable individuals are affected directly or indirectly.",
        "Define prohibited patterns and higher-control scenarios.",
        "Implement age, context, or sensitivity safeguards where appropriate.",
        "Require enhanced review for high-risk messaging, targeting, or intervention uses.",
        "Train teams on vulnerability-related escalation.",
        "Monitor for harmful interactions or complaints.",
        "Document containment and corrective actions when issues arise.",
        "Retain evidence showing vulnerable-user risk was actively managed.",
      ],
      legalChecklist: [
        "Vulnerability assessment.",
        "Safeguard rules.",
        "Escalation process.",
        "Incident or complaint records.",
        "Corrective action evidence.",
      ],
      evidenceFocus: [
        "Policy extracts.",
        "Age/context controls.",
        "Training materials.",
        "Complaint or incident tickets.",
        "Leadership escalations.",
      ],
    },
    BIOMETRIC_IDENTIFICATION: {
      id: "BIOMETRIC_IDENTIFICATION",
      label: "Biometric Identification",
      summary:
        "Apply heightened control where biometric identification, matching, or categorization may create legal, privacy, or civil-liberties risk.",
      priorityActions: {
        LOW: [
          "Confirm whether biometric data or matching is actually used anywhere in the workflow.",
          "Document the specific purpose and current safeguards.",
          "Review whether less intrusive alternatives exist.",
        ],
        MEDIUM: [
          "Require formal approval and risk assessment before continued use.",
          "Tighten consent, access, retention, and oversight controls.",
          "Limit use to clearly approved and necessary contexts.",
        ],
        HIGH: [
          "Suspend the affected biometric use immediately until legal basis and controls are verified.",
          "Escalate to legal, privacy, and leadership review with preserved evidence.",
          "Assess whether prior uses require notification, correction, or rollback.",
        ],
      },
      fullActions: [
        "Map where biometric capture, matching, identification, or categorization occurs.",
        "Document purpose, necessity, and lawful basis.",
        "Apply strict access, retention, and audit controls.",
        "Restrict deployment contexts and downstream uses.",
        "Require enhanced review and approval for changes.",
        "Monitor for misuse, overreach, or inappropriate expansion.",
        "Train responsible teams on heightened obligations.",
        "Retain evidence of approvals, safeguards, and oversight.",
      ],
      legalChecklist: [
        "Biometric use map.",
        "Risk assessment.",
        "Approval records.",
        "Access/retention controls.",
        "Oversight evidence.",
      ],
      evidenceFocus: [
        "Data flow diagrams.",
        "Privacy/legal approvals.",
        "Access-control extracts.",
        "Retention settings.",
        "Monitoring records.",
      ],
    },
    MANIPULATIVE_DECEPTIVE_AI: {
      id: "MANIPULATIVE_DECEPTIVE_AI",
      label: "Manipulative or Deceptive AI",
      summary:
        "Prevent AI behaviors or deployment choices that mislead users, exploit cognitive weaknesses, or drive inappropriate decisions.",
      priorityActions: {
        LOW: [
          "Review current product behavior and messaging for subtle deception or undue influence risks.",
          "Document the most sensitive persuasion or intervention points.",
          "Clarify acceptable and unacceptable practices for the team.",
        ],
        MEDIUM: [
          "Remove or redesign features that could materially mislead users about AI capability or intent.",
          "Add stronger disclosures and guardrails around persuasive or intervention-oriented content.",
          "Require governance review for similar future features.",
        ],
        HIGH: [
          "Disable the affected feature immediately if manipulation or deception may be occurring.",
          "Escalate to legal/compliance and preserve evidence of the user experience.",
          "Review whether prior outputs or interventions created consumer harm.",
        ],
      },
      fullActions: [
        "Assess where the AI may influence user choices, beliefs, or behavior.",
        "Prohibit misleading representations about capability, certainty, or identity.",
        "Add safeguards for persuasion-heavy or emotionally sensitive interactions.",
        "Require review for prompts, scripts, or interventions with heightened influence risk.",
        "Train teams on manipulative pattern avoidance.",
        "Monitor complaints and abnormal engagement patterns.",
        "Retain evidence of design review and corrective changes.",
        "Escalate borderline cases rather than normalizing them.",
      ],
      legalChecklist: [
        "Design-risk review.",
        "Disclosure and script approvals.",
        "Complaint monitoring outputs.",
        "Corrective design change records.",
        "Escalation notes.",
      ],
      evidenceFocus: [
        "UX screenshots.",
        "Prompt/script versions.",
        "Review sign-offs.",
        "Complaint examples.",
        "Change tickets.",
      ],
    },
    THIRD_PARTY_VENDOR_RISK: {
      id: "THIRD_PARTY_VENDOR_RISK",
      label: "Vendor and Third-Party AI Risk",
      summary:
        "Control the external dependencies, contracts, oversight, and fallback planning associated with third-party AI tools and providers.",
      priorityActions: {
        LOW: [
          "List the third-party AI services involved and confirm who owns the relationship.",
          "Check whether basic contractual and security reviews are on file.",
          "Document key dependency risks.",
        ],
        MEDIUM: [
          "Refresh vendor review for the affected service, including data, security, and continuity implications.",
          "Confirm contracts, DPAs, and service commitments reflect actual usage.",
          "Define fallback steps if the service fails or changes materially.",
        ],
        HIGH: [
          "Escalate immediately if the vendor creates unresolved legal, security, or operational exposure.",
          "Limit or suspend the dependency until key controls or contractual gaps are closed.",
          "Preserve evidence of vendor communications and prior approvals.",
        ],
      },
      fullActions: [
        "Maintain an inventory of third-party AI providers and linked business processes.",
        "Assess vendor security, privacy, resilience, and compliance commitments.",
        "Confirm contracts, DPAs, and use restrictions are current.",
        "Document approved use, prohibited use, and escalation contacts.",
        "Set review triggers for material vendor changes.",
        "Plan fallback or exit options for critical dependencies.",
        "Monitor vendor incidents and performance issues.",
        "Retain evidence of vendor oversight and approval decisions.",
      ],
      legalChecklist: [
        "Vendor inventory.",
        "Due diligence records.",
        "Contract/DPA copies.",
        "Fallback planning.",
        "Ongoing review evidence.",
      ],
      evidenceFocus: [
        "Vendor questionnaires.",
        "Approval notes.",
        "Contract extracts.",
        "Incident communications.",
        "Review schedules.",
      ],
    },
    AUDIT_READINESS: {
      id: "AUDIT_READINESS",
      label: "Audit Readiness",
      summary:
        "Ensure the business can explain its AI control environment quickly and coherently if challenged by customers, regulators, or internal reviewers.",
      priorityActions: {
        LOW: [
          "Identify the highest-priority evidence gaps for the affected workflow.",
          "Confirm who would respond if an audit or challenge arrived today.",
          "Assemble the current core documents in one place.",
        ],
        MEDIUM: [
          "Create a standard evidence pack covering governance, testing, monitoring, and remediation.",
          "Run a dry-run retrieval exercise to check whether materials are complete.",
          "Track missing evidence items to closure.",
        ],
        HIGH: [
          "Treat missing audit evidence as an immediate defensibility issue.",
          "Launch a focused evidence reconstruction and quality review sprint.",
          "Escalate unresolved readiness gaps to leadership with dates and owners.",
        ],
      },
      fullActions: [
        "Define the minimum evidence pack for an AI control review.",
        "Assign ownership for each evidence category.",
        "Centralize core materials or maintain a clear retrieval map.",
        "Test whether evidence can be produced quickly and coherently.",
        "Track evidence gaps and remediation dates.",
        "Refresh the evidence pack after major product or control changes.",
        "Use audit-readiness reviews to improve day-to-day control discipline.",
        "Maintain an executive summary of current readiness posture.",
      ],
      legalChecklist: [
        "Evidence pack index.",
        "Ownership matrix.",
        "Retrieval test outputs.",
        "Gap tracker.",
        "Management reporting evidence.",
      ],
      evidenceFocus: [
        "Evidence inventories.",
        "Shared repository map.",
        "Dry-run results.",
        "Gap closure tickets.",
        "Leadership updates.",
      ],
    },
    LOGGING_TRACEABILITY: {
      id: "LOGGING_TRACEABILITY",
      label: "Logging and Traceability",
      summary:
        "Keep sufficient logs and traceability so the organization can reconstruct what happened and why.",
      priorityActions: {
        LOW: [
          "Confirm what is currently logged and where traceability is weak.",
          "Identify the minimum fields needed to reconstruct material events.",
          "Assign ownership for log review and retention.",
        ],
        MEDIUM: [
          "Expand logging for approvals, prompts, outputs, interventions, and material changes where appropriate.",
          "Set retention and access rules for traceability records.",
          "Verify logs can be correlated across systems when needed.",
        ],
        HIGH: [
          "Preserve relevant logs immediately if an incident, complaint, or regulatory challenge exists.",
          "Escalate missing traceability as a critical defensibility weakness.",
          "Implement interim logging controls before the workflow continues to scale.",
        ],
      },
      fullActions: [
        "Define the events and fields that must be logged for the use case.",
        "Capture sufficient data to reconstruct prompts, outputs, approvals, and interventions where lawful and appropriate.",
        "Protect logs against unauthorized alteration or loss.",
        "Set retention and retrieval rules.",
        "Review whether traceability is adequate for high-stakes scenarios.",
        "Link logs to incident handling and complaint review.",
        "Test retrieval periodically.",
        "Document exceptions where full logging is not feasible and apply compensating controls.",
      ],
      legalChecklist: [
        "Logging standard.",
        "Retention rules.",
        "Access controls.",
        "Retrieval test outputs.",
        "Exception records.",
      ],
      evidenceFocus: [
        "Log schema or config.",
        "Retention settings.",
        "Retrieval screenshots.",
        "Access-control records.",
        "Exception approvals.",
      ],
    },
    CHANGE_MANAGEMENT: {
      id: "CHANGE_MANAGEMENT",
      label: "Change Management",
      summary:
        "Control how AI models, prompts, integrations, and policies change so risk is not introduced informally.",
      priorityActions: {
        LOW: [
          "Document the current change path for the affected system.",
          "Clarify which types of changes should trigger review.",
          "Close obvious gaps in release documentation.",
        ],
        MEDIUM: [
          "Require documented approval for material model, prompt, policy, or integration changes.",
          "Link changes to testing and rollback planning.",
          "Version releases so production history is clear.",
        ],
        HIGH: [
          "Stop unmanaged changes immediately in the affected workflow.",
          "Reconstruct recent changes and identify any that bypassed review.",
          "Escalate to leadership if unauthorized changes may have created material exposure.",
        ],
      },
      fullActions: [
        "Define what constitutes a material AI change.",
        "Require review and approval before promotion of material changes.",
        "Link changes to validation, monitoring, and rollback plans.",
        "Maintain version history for models, prompts, tools, and policies.",
        "Record who approved what and when.",
        "Periodically review for bypassed or undocumented changes.",
        "Use post-release review to validate that controls remained effective.",
        "Retain evidence supporting each material change.",
      ],
      legalChecklist: [
        "Change criteria.",
        "Approval records.",
        "Version history.",
        "Rollback plans.",
        "Post-release review evidence.",
      ],
      evidenceFocus: [
        "Release notes.",
        "Tickets.",
        "Approval screenshots.",
        "Version tags.",
        "Rollback/test outputs.",
      ],
    },
    AI_SYSTEM_INVENTORY: {
      id: "AI_SYSTEM_INVENTORY",
      label: "AI System Inventory",
      summary:
        "Maintain a current inventory of AI systems so leadership can see where risk exists and where controls apply.",
      priorityActions: {
        LOW: [
          "Identify the AI systems currently in scope and confirm naming/ownership consistency.",
          "Fill obvious inventory gaps for live use cases.",
          "Capture the main business purpose for each system.",
        ],
        MEDIUM: [
          "Implement a standard inventory record covering owner, purpose, data, users, and risk level.",
          "Require new AI use cases to be registered before go-live.",
          "Review the inventory periodically for stale or missing entries.",
        ],
        HIGH: [
          "Treat unknown or unregistered AI use cases as an immediate governance issue.",
          "Freeze expansion until critical inventory blind spots are corrected.",
          "Escalate shadow-AI findings to leadership with ownership assignments.",
        ],
      },
      fullActions: [
        "Create and maintain a centralized AI system inventory.",
        "Capture owner, purpose, users, data, jurisdictional scope, and dependency information.",
        "Link inventory items to approvals, testing, monitoring, and incidents.",
        "Require registration before deployment or material expansion.",
        "Review inventory completeness on a defined cadence.",
        "Retire stale entries and flag shadow or unauthorized use.",
        "Use the inventory to prioritize oversight resources.",
        "Retain evidence of inventory review and updates.",
      ],
      legalChecklist: [
        "Inventory schema.",
        "Current inventory extract.",
        "Registration workflow.",
        "Review cadence records.",
        "Shadow-AI remediation notes.",
      ],
      evidenceFocus: [
        "Inventory exports.",
        "Owner attestations.",
        "Workflow screenshots.",
        "Review reports.",
        "Gap closure tickets.",
      ],
    },
    GENERAL_COMPLIANCE: {
      id: "GENERAL_COMPLIANCE",
      label: "General Compliance Controls",
      summary:
        "Stabilize the control environment with practical governance, documentation, and review actions where the issue does not fit a narrower playbook cleanly.",
      priorityActions: {
        LOW: [
          "Clarify the control gap and assign a business owner.",
          "Document the current process and largest unresolved weakness.",
          "Set a realistic review date.",
        ],
        MEDIUM: [
          "Create a documented remediation plan with owner, date, and verification step.",
          "Tighten approvals, records, and review cadence around the affected workflow.",
          "Retest or re-review the area once changes are complete.",
        ],
        HIGH: [
          "Escalate the issue as a material control gap requiring immediate containment and leadership oversight.",
          "Pause non-essential scaling until the weakness is addressed.",
          "Preserve evidence and review recent affected activity for spillover risk.",
        ],
      },
      fullActions: [
        "Assign ownership and define the remediation outcome clearly.",
        "Document the current state, target state, and key deadlines.",
        "Collect evidence showing the issue was understood and actioned.",
        "Strengthen approvals, testing, monitoring, or records as needed.",
        "Escalate unresolved blockers promptly.",
        "Retest after remediation.",
        "Update leadership on remaining residual risk.",
        "Retain closure evidence for future review.",
      ],
      legalChecklist: [
        "Issue statement.",
        "Owner assignment.",
        "Remediation plan.",
        "Retest record.",
        "Closure evidence.",
      ],
      evidenceFocus: [
        "Remediation tracker.",
        "Approvals.",
        "Test evidence.",
        "Leadership updates.",
        "Closure sign-off.",
      ],
    },
  };
  
  const CATEGORY_HINTS = {
    AI_RISK_MANAGEMENT: ["risk management", "risk framework", "risk register", "framework", "governance framework"],
    HUMAN_OVERSIGHT: ["human oversight", "human review", "override", "manual review", "human-in-the-loop"],
    ACCOUNTABILITY_GOVERNANCE: ["governance", "accountability", "ownership", "board", "oversight committee"],
    INCIDENT_REPORTING: ["incident", "breach", "near miss", "escalation", "reporting"],
    TRAINING_DATA_GOVERNANCE: ["training data", "fine-tuning", "data source", "provenance", "dataset sourcing"],
    DATASET_BIAS: ["bias", "representative", "representativeness", "fairness", "discrimination", "dataset bias"],
    DATA_PROTECTION_PRIVACY: ["privacy", "personal data", "data protection", "consent", "retention", "sensitive data"],
    RECORDKEEPING_RETENTION: ["recordkeeping", "retention", "records", "documentation retention", "evidence retention"],
    MODEL_TESTING_VALIDATION: ["testing", "validation", "benchmark", "quality assurance", "evaluation"],
    MONITORING_DRIFT: ["monitoring", "drift", "performance monitoring", "post-deployment", "alerting"],
    SECURITY_ROBUSTNESS: ["security", "adversarial", "prompt injection", "access control", "robustness", "abuse"],
    MODEL_DOCUMENTATION: ["documentation", "traceability", "model card", "technical file", "system documentation"],
    TRANSPARENCY_DISCLOSURE: ["transparency", "disclosure", "notice", "user notice", "explain that ai is used"],
    EXPLAINABILITY: ["explainability", "explainable", "reasons", "rationale", "interpretability"],
    USER_REDRESS: ["redress", "complaint", "appeal", "challenge", "human appeal"],
    SOCIAL_SCORING: ["social scoring", "scoring people", "profiling", "ranking people"],
    VULNERABLE_PERSONS: ["vulnerable", "children", "minors", "disability", "distress"],
    BIOMETRIC_IDENTIFICATION: ["biometric", "facial recognition", "face match", "voiceprint", "identification"],
    MANIPULATIVE_DECEPTIVE_AI: ["manipulative", "deceptive", "dark pattern", "mislead", "persuasion"],
    THIRD_PARTY_VENDOR_RISK: ["vendor", "third party", "supplier", "processor", "external provider"],
    AUDIT_READINESS: ["audit readiness", "audit", "exam", "defensibility", "evidence pack"],
    LOGGING_TRACEABILITY: ["logging", "logs", "traceability", "audit trail", "prompt logs"],
    CHANGE_MANAGEMENT: ["change management", "release", "deployment change", "version control", "rollback"],
    AI_SYSTEM_INVENTORY: ["inventory", "register", "system inventory", "use case inventory", "catalogue"],
    GENERAL_COMPLIANCE: ["general", "assessment", "control", "compliance"],
  };
  
  const REGIME_LABELS = {
    EU: "EU",
    UK: "UK",
    US_FED: "US (Federal)",
    US_STATE: "US (State)",
  };
  
  const REGIME_BASELINES = {
    EU: {
      executiveNotes: [
        "For EU-scoped activity, preserve a defensible governance file showing risk review, accountability, and post-deployment control evidence.",
      ],
      legalNotes: [
        "For EU-scoped activity, document the system role, affected users, risk rationale, and the operating controls relied upon in practice.",
      ],
      checklistAdditions: [
        "Document the system purpose, affected users, deployment context, and responsible owner for EU-facing use.",
        "Retain the current risk review, control rationale, and approval record for the affected use case.",
      ],
      evidenceAdditions: [
        "EU-facing governance memo or risk review record.",
        "Versioned control inventory showing which safeguards were active at the time.",
      ],
    },
    UK: {
      executiveNotes: [
        "For UK-scoped activity, emphasize accountable ownership, defensible user outcomes, and evidence that controls are proportionate and operating.",
      ],
      legalNotes: [
        "For UK-scoped activity, keep a clear record of decision ownership, user impact, complaints handling, and remediation follow-through.",
      ],
      checklistAdditions: [
        "Record the accountable UK business owner and the escalation path for the affected process.",
        "Capture user impact, complaints, and remediation follow-up for the affected workflow.",
      ],
      evidenceAdditions: [
        "UK governance or risk-committee record.",
        "Complaint-handling or customer remediation evidence where relevant.",
      ],
    },
    US_FED: {
      executiveNotes: [
        "For US federal exposure, focus on substantiation, consumer impact, internal approvals, and proof that representations match real system behavior.",
      ],
      legalNotes: [
        "For US federal exposure, preserve evidence supporting product claims, review steps, and corrective action taken after issues were found.",
      ],
      checklistAdditions: [
        "Document the basis for any claims, marketing statements, or internal assertions about system performance and safety.",
        "Retain approval records showing who authorized launch, material changes, and corrective actions.",
      ],
      evidenceAdditions: [
        "Claim-substantiation file, testing support, or validation summary.",
        "US federal review notes or leadership approval trail.",
      ],
    },
    US_STATE: {
      executiveNotes: [
        "For US state exposure, emphasize notices, consumer-facing practices, data handling, and vendor/accountability records that can be produced quickly.",
      ],
      legalNotes: [
        "For US state exposure, preserve notice language, data-flow evidence, vendor terms, and the records supporting consumer-rights handling where applicable.",
      ],
      checklistAdditions: [
        "Retain the live notice, disclosure, or policy language applicable to the affected state-facing workflow.",
        "Document vendor/data-flow responsibilities and any consumer-rights handling steps tied to the issue.",
      ],
      evidenceAdditions: [
        "State-facing notice or privacy language in force at the time.",
        "Vendor clause, DPA, or workflow record supporting state compliance handling.",
      ],
    },
  };
  
  const PLAYBOOK_JURISDICTION_OVERLAYS = {
    DATA_PROTECTION_PRIVACY: {
      EU: {
        executiveNotes: [
          "Tighten lawful-use, retention, and access-control documentation for EU personal-data handling.",
        ],
        legalNotes: [
          "Preserve the data-flow narrative, lawful-use rationale, retention logic, and vendor/control boundaries for the affected processing.",
        ],
        checklistAdditions: [
          "Document the categories of personal data involved and the purpose for which they were used.",
          "Retain retention and deletion rules that applied to the affected data set or workflow.",
        ],
        evidenceAdditions: [
          "Data-flow map, retention schedule, or processing note for the affected workflow.",
        ],
      },
      UK: {
        checklistAdditions: [
          "Capture the UK-facing privacy notice, data-use rationale, and ownership for the affected process.",
        ],
        evidenceAdditions: [
          "UK-facing privacy notice or internal data-handling approval record.",
        ],
      },
      US_STATE: {
        checklistAdditions: [
          "Retain state-facing privacy notice language and consumer-rights handling steps relevant to the issue.",
        ],
        evidenceAdditions: [
          "State privacy notice, intake record, or consumer-rights workflow evidence.",
        ],
      },
    },
    TRANSPARENCY_DISCLOSURE: {
      EU: {
        executiveNotes: [
          "Strengthen disclosure records showing when users were informed that AI was involved and what limitations were explained.",
        ],
        checklistAdditions: [
          "Retain the exact disclosure or notice language shown to users in the affected flow.",
        ],
        evidenceAdditions: [
          "Screenshots or archived UX text showing the live disclosure language.",
        ],
      },
      UK: {
        checklistAdditions: [
          "Capture customer-facing wording and complaint/escalation handling for misleading or incomplete disclosure concerns.",
        ],
      },
      US_FED: {
        checklistAdditions: [
          "Document why product or marketing statements about the AI experience were accurate and supportable at the time used.",
        ],
        evidenceAdditions: [
          "Claim-support file matching user-facing statements to the actual system behavior.",
        ],
      },
      US_STATE: {
        checklistAdditions: [
          "Retain state-facing notices, consent language, or call-center scripts relevant to the affected user journey.",
        ],
      },
    },
    HUMAN_OVERSIGHT: {
      EU: {
        executiveNotes: [
          "For EU-facing high-impact use, demonstrate that human review is real, documented, and able to override or stop unsafe outputs.",
        ],
        checklistAdditions: [
          "Record the human review checkpoint, reviewer role, and override authority for the affected workflow.",
        ],
        evidenceAdditions: [
          "Reviewer sign-off, override log, or sampling record for affected outputs.",
        ],
      },
      UK: {
        checklistAdditions: [
          "Preserve records showing who reviewed exceptions, complaints, or adverse outcomes and what they changed.",
        ],
      },
      US_FED: {
        checklistAdditions: [
          "Retain evidence that manual review existed where the business represented that human review or safeguards were in place.",
        ],
      },
    },
    AI_RISK_MANAGEMENT: {
      EU: {
        checklistAdditions: [
          "Retain the current governance file, risk review, and control-tracking record for EU-scoped use cases.",
        ],
      },
      UK: {
        checklistAdditions: [
          "Document accountable UK ownership and the ongoing review cadence for the affected use case.",
        ],
      },
      US_FED: {
        checklistAdditions: [
          "Preserve approval and review records supporting management claims about AI safety, control, or oversight maturity.",
        ],
      },
      US_STATE: {
        checklistAdditions: [
          "Document the state-facing control owner, applicable notice set, and vendor/accountability dependencies where relevant.",
        ],
      },
    },
    BIOMETRIC_IDENTIFICATION: {
      EU: {
        executiveNotes: [
          "Biometric use in EU-scoped contexts requires immediate leadership attention, tight containment, and a defensible record of scope and safeguards.",
        ],
        legalNotes: [
          "Preserve exact use context, affected individuals, deployment scope, and the approval or suspension decision taken after identification of the issue.",
        ],
        checklistAdditions: [
          "Document where biometric capability was active, who was affected, and whether use was paused or restricted.",
        ],
        evidenceAdditions: [
          "Deployment scope record, screenshots, and suspension/containment evidence for biometric functionality.",
        ],
      },
      US_STATE: {
        checklistAdditions: [
          "Preserve state-facing notice, consent, retention, and vendor-handling records relevant to biometric data or templates.",
        ],
        evidenceAdditions: [
          "Biometric notice/consent record and retention rule in force for the affected workflow.",
        ],
      },
    },
    VULNERABLE_PERSONS: {
      EU: {
        checklistAdditions: [
          "Document whether minors or other vulnerable individuals were in scope and what heightened safeguards were active.",
        ],
      },
      UK: {
        checklistAdditions: [
          "Preserve records showing how the business assessed and mitigated the effect on children or other vulnerable groups.",
        ],
      },
      US_STATE: {
        checklistAdditions: [
          "Retain state-facing child/privacy notice language and any parental or guardian handling steps relevant to the workflow.",
        ],
      },
    },
  };
  
  export function normalizeJurisdictionKey(raw) {
    const value = upper(raw);
    if (!value) return "";
    if (value === "EU" || value === "EEA") return "EU";
    if (value === "UK" || value === "UNITED_KINGDOM" || value === "GB") return "UK";
    if (value === "US" || value === "US_FED" || value === "USA" || value === "UNITED_STATES") return "US_FED";
    if (value === "US_STATE" || value === "STATE") return "US_STATE";
    return value;
  }
  
  function getOverlayCandidatesForJurisdictions(jurisdictions) {
    return uniqStrings(jurisdictions).map(normalizeJurisdictionKey).filter((key) => REGIME_BASELINES[key]);
  }
  
  function mergeOverlayField(items, field) {
    return uniqStrings(items.flatMap((item) => safeArray(item?.[field])));
  }
  
  function buildJurisdictionOverlayPack(playbook, jurisdictions) {
    const regimeKeys = getOverlayCandidatesForJurisdictions(jurisdictions);
    if (!regimeKeys.length) {
      return {
        regimeKeys: [],
        regimeLabels: [],
        executiveNotes: [],
        legalNotes: [],
        checklistAdditions: [],
        evidenceAdditions: [],
        summaryLine: "",
      };
    }
  
    const playbookOverlays = safeObj(PLAYBOOK_JURISDICTION_OVERLAYS?.[playbook?.id]);
    const mergedItems = regimeKeys.map((key) => ({
      executiveNotes: [
        ...safeArray(REGIME_BASELINES?.[key]?.executiveNotes),
        ...safeArray(playbookOverlays?.[key]?.executiveNotes),
      ],
      legalNotes: [
        ...safeArray(REGIME_BASELINES?.[key]?.legalNotes),
        ...safeArray(playbookOverlays?.[key]?.legalNotes),
      ],
      checklistAdditions: [
        ...safeArray(REGIME_BASELINES?.[key]?.checklistAdditions),
        ...safeArray(playbookOverlays?.[key]?.checklistAdditions),
      ],
      evidenceAdditions: [
        ...safeArray(REGIME_BASELINES?.[key]?.evidenceAdditions),
        ...safeArray(playbookOverlays?.[key]?.evidenceAdditions),
      ],
    }));
  
    const regimeLabels = regimeKeys.map((key) => REGIME_LABELS[key] || key);
    const executiveNotes = mergeOverlayField(mergedItems, "executiveNotes");
    const legalNotes = mergeOverlayField(mergedItems, "legalNotes");
    const checklistAdditions = mergeOverlayField(mergedItems, "checklistAdditions");
    const evidenceAdditions = mergeOverlayField(mergedItems, "evidenceAdditions");
  
    return {
      regimeKeys,
      regimeLabels,
      executiveNotes,
      legalNotes,
      checklistAdditions,
      evidenceAdditions,
      summaryLine: regimeLabels.length
        ? `Regime emphasis: ${regimeLabels.join(", ")}.`
        : "",
    };
  }
  
  function scorePlaybook(playbookId, haystack) {
    const hints = safeArray(CATEGORY_HINTS[playbookId]);
    return hints.reduce((score, hint) => {
      if (!hint) return score;
      if (haystack.includes(hint)) return score + Math.max(2, hint.length / 12);
      return score;
    }, 0);
  }
  
  function buildHaystack(findingLike = {}) {
    const f = safeObj(findingLike);
    return [f.category, f.title, f.text, f.guidance]
      .map((x) => str(x).toLowerCase())
      .filter(Boolean)
      .join(" | ");
  }
  
  export function resolvePlaybookForFinding(findingLike = {}) {
    const haystack = buildHaystack(findingLike);
    const explicit = upper(findingLike?.playbook_id);
    if (explicit && PLAYBOOKS[explicit]) return PLAYBOOKS[explicit];
  
    let bestId = "GENERAL_COMPLIANCE";
    let bestScore = 0;
    Object.keys(PLAYBOOKS).forEach((id) => {
      const score = scorePlaybook(id, haystack);
      if (score > bestScore) {
        bestScore = score;
        bestId = id;
      }
    });
    return PLAYBOOKS[bestId] || PLAYBOOKS.GENERAL_COMPLIANCE;
  }
  
  function buildPriorityBucket(playbook, gravity) {
    const band = getGravityBand(gravity);
    if (band === GRAVITY_BANDS.CRITICAL || band === GRAVITY_BANDS.HIGH) {
      return safeArray(playbook?.priorityActions?.HIGH);
    }
    if (band === GRAVITY_BANDS.SIGNIFICANT) {
      return safeArray(playbook?.priorityActions?.MEDIUM);
    }
    return safeArray(playbook?.priorityActions?.LOW);
  }
  
  function buildGuidanceText({ playbook, gravity, jurisdictions, overlayPack }) {
    const priority = buildPriorityBucket(playbook, gravity);
    const fullActions = safeArray(playbook?.fullActions);
    const regimeNote = uniqStrings(jurisdictions)
      .map((j) => str(j))
      .filter(Boolean)
      .join(", ");
    const executiveOverlayNotes = safeArray(overlayPack?.executiveNotes);
  
    const parts = [
      `${playbook?.label || "Recommended Remediation"}`,
      str(playbook?.summary),
      `Urgency: ${getGravityBandLabel(gravity)} (${normalizeGravityValue(gravity)}/10).`,
    ];
  
    if (priority.length) {
      parts.push("Immediate Priorities");
      parts.push(sentenceList(priority));
    }
  
    if (fullActions.length) {
      parts.push("Core Control Actions");
      parts.push(sentenceList(fullActions));
    }
  
    if (executiveOverlayNotes.length) {
      parts.push("Regime Emphasis");
      parts.push(sentenceList(executiveOverlayNotes));
    }
  
    if (regimeNote) {
      parts.push(`Applicable regimes in this assessment: ${regimeNote}.`);
    }
  
    return parts.filter(Boolean).join("\n\n");
  }
  
  export function buildRemediationForFinding(findingLike = {}, options = {}) {
    const finding = safeObj(findingLike);
    const playbook = resolvePlaybookForFinding(finding);
    const jurisdictions = uniqStrings(options?.jurisdictions || finding?.jurisdictions);
    const gravityValue = normalizeGravityValue(options?.gravity ?? finding?.gravity);
    const gravityBand = getGravityBand(gravityValue);
    const overlayPack = buildJurisdictionOverlayPack(playbook, jurisdictions);
  
    return {
      playbookId: playbook.id,
      playbookLabel: playbook.label,
      summary: playbook.summary,
      gravityValue,
      gravityBand,
      gravityBandLabel: getGravityBandLabel(gravityValue),
      priorityActions: buildPriorityBucket(playbook, gravityValue).slice(0, 3),
      fullActions: safeArray(playbook.fullActions),
      legalChecklist: uniqStrings([
        ...safeArray(playbook.legalChecklist),
        ...safeArray(overlayPack.checklistAdditions),
      ]),
      evidenceFocus: uniqStrings([
        ...safeArray(playbook.evidenceFocus),
        ...safeArray(overlayPack.evidenceAdditions),
      ]),
      jurisdictions,
      regimeKeys: safeArray(overlayPack.regimeKeys),
      regimeLabels: safeArray(overlayPack.regimeLabels),
      executiveNotes: safeArray(overlayPack.executiveNotes),
      legalNotes: safeArray(overlayPack.legalNotes),
      overlaySummaryLine: String(overlayPack.summaryLine || ""),
      guidanceText: buildGuidanceText({ playbook, gravity: gravityValue, jurisdictions, overlayPack }),
    };
  }
  
  export function getAllRemediationPlaybooks() {
    return Object.values(PLAYBOOKS);
  }